import { useEffect, useState, useCallback, useMemo } from "react"
import { apiClient } from "@/lib/api"
import type {
  ReviewIntelligenceResponse,
  ReviewAnalysis,
  ReviewNumericContext,
  SeasonProfileResponse,
  BookingReviewItem,
  BookingReviewSummary,
  AirbnbCategoryScores,
} from "@/lib/api"

// --- Types ---

export type ReviewStatus = "pending" | "running" | "stale" | "ok" | "error"

export interface ReviewSource {
  id: string
  source: string
  source_url: string
  status: ReviewStatus
  apify_run_id: string
  apify_dataset_id: string
  last_error: string
  last_synced_at: string
}

export interface ReviewSummary {
  reviews_count: number
  average_rating: number | null
  category_scores?: AirbnbCategoryScores
}

export interface ReviewItem {
  id: string
  reviewer_name: string
  reviewer_image_url: string
  rating: number
  text: string
  localized_text: string
  language: string
  created_at: string
}

// --- Helpers ---

export function formatReviewDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  } catch {
    return dateStr
  }
}

export function formatTimeAgo(dateStr: string | undefined): string {
  if (!dateStr) return ""
  try {
    const d = new Date(dateStr)
    const now = Date.now()
    const diffH = Math.round((now - d.getTime()) / 3600000)
    if (diffH < 1) return "just now"
    if (diffH < 24) return `${diffH}h ago`
    const diffD = Math.round(diffH / 24)
    if (diffD === 1) return "yesterday"
    return `${diffD}d ago`
  } catch {
    return ""
  }
}

export function formatRecencyDays(days: number): string {
  if (days < 1) return "Today"
  if (days === 1) return "Yesterday"
  if (days < 14) return `${days}d ago`
  if (days < 60) return `${Math.round(days / 7)}w ago`
  return `${Math.round(days / 30)}mo ago`
}

export function themeDisplayName(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export function velocityMonthly(perQuarter: number): string {
  return (perQuarter / 3).toFixed(1)
}

export function deriveTrend(nc: ReviewNumericContext | undefined): "improving" | "stable" | "softening" {
  if (!nc || nc.avg_oldest_third == null || nc.avg_newest_third == null) return "stable"
  const diff = nc.avg_newest_third - nc.avg_oldest_third
  if (diff > 0.3) return "improving"
  if (diff < -0.3) return "softening"
  return "stable"
}

export function guestTypeSummary(guestTypes: Array<{ type: string; count: number }>, n: number): string {
  const top = guestTypes
    .filter((g) => g.count > 0)
    .slice(0, n)
    .map((g) => themeDisplayName(g.type).toLowerCase())
  if (top.length === 0) return ""
  if (top.length === 1) return `Mostly ${top[0]}`
  return `Mostly ${top.slice(0, -1).join(", ")} and ${top[top.length - 1]}`
}

// --- Season-Aware Helpers ---

function currentMonth(): number {
  return new Date().getMonth() + 1
}

export function isOffSeason(sp: SeasonProfileResponse | null): boolean {
  if (!sp || !sp.off_season_months || sp.off_season_months.length === 0) return false
  if (sp.classification === "year_round") return false
  return sp.off_season_months.includes(currentMonth())
}

export function isPeakSeason(sp: SeasonProfileResponse | null): boolean {
  if (!sp || !sp.peak_months || sp.peak_months.length === 0) return false
  if (sp.classification === "year_round") return false
  return sp.peak_months.includes(currentMonth())
}

// --- Momentum Summary ---

export function momentumSummary(
  analysis: ReviewAnalysis,
  nc: ReviewAnalysis["_numeric_context"],
  bnc: ReviewAnalysis["_booking_numeric_context"],
  seasonProfile: SeasonProfileResponse | null,
): string {
  const parts: string[] = []
  const hasBoth = nc?.avg_newest_third != null && bnc?.avg_newest_third != null

  if (hasBoth) {
    const airbnbAvg = nc!.avg_newest_third!.toFixed(1)
    const bookingAvg = bnc!.avg_newest_third!.toFixed(1)
    const airbnbTrend = analysis.sentiment_trend
    const bookingTrend = deriveTrend(bnc)
    const trendVerb = (t: string) =>
      t === "improving" ? "trending up to" : t === "softening" ? "drifting to" : "steady at"
    parts.push(`Airbnb ${trendVerb(airbnbTrend)} ${airbnbAvg}`)
    parts.push(`Booking.com ${trendVerb(bookingTrend)} ${bookingAvg}/10`)
  } else if (nc?.avg_newest_third != null) {
    const avg = nc.avg_newest_third.toFixed(1)
    if (analysis.sentiment_trend === "improving") parts.push(`Ratings trending up to ${avg}`)
    else if (analysis.sentiment_trend === "softening") parts.push(`Ratings drifting down to ${avg}`)
    else parts.push(`Ratings holding steady at ${avg}`)
  } else if (bnc?.avg_newest_third != null) {
    const avg = bnc.avg_newest_third.toFixed(1)
    const trend = deriveTrend(bnc)
    if (trend === "improving") parts.push(`Booking.com trending up to ${avg}/10`)
    else if (trend === "softening") parts.push(`Booking.com drifting to ${avg}/10`)
    else parts.push(`Booking.com steady at ${avg}/10`)
  } else {
    if (analysis.sentiment_trend === "improving") parts.push("Ratings improving")
    else if (analysis.sentiment_trend === "softening") parts.push("Ratings softening")
    else parts.push("Ratings stable")
  }

  if (!hasBoth && nc?.rating_stdev != null) {
    if (nc.rating_stdev < 0.3) parts[0] += " with tight consistency"
    else if (nc.rating_stdev > 0.6) parts[0] += " but inconsistent"
  }

  const airbnbVelQ = nc?.review_velocity_per_quarter ?? 0
  const bookingVelQ = bnc?.review_velocity_per_quarter ?? 0
  const combinedVelQ = airbnbVelQ + bookingVelQ
  if (combinedVelQ > 0) {
    const perMonth = combinedVelQ / 3
    const velStr = perMonth.toFixed(1)
    if (isOffSeason(seasonProfile)) parts.push(`${velStr}/mo flow during off-season is expected`)
    else if (isPeakSeason(seasonProfile) && perMonth < 1) parts.push(`${velStr}/mo is below peak-season pace`)
    else if (perMonth >= 3) parts.push(`${velStr} reviews/mo — accelerating`)
    else if (perMonth >= 1) parts.push(`${velStr} reviews/mo — steady flow`)
    else parts.push(`${velStr}/mo — review flow slow`)
  }

  return parts.join(". ") + "."
}

// --- Data Hook ---

export function useReviewsPageData(propertyId: string | undefined) {
  const [source, setSource] = useState<ReviewSource | null>(null)
  const [summary, setSummary] = useState<ReviewSummary | null>(null)
  const [reviews, setReviews] = useState<ReviewItem[] | null>(null)
  const [intel, setIntel] = useState<ReviewIntelligenceResponse | null>(null)
  const [seasonProfile, setSeasonProfile] = useState<SeasonProfileResponse | null>(null)
  const [bookingSource, setBookingSource] = useState<ReviewSource | null>(null)
  const [bookingSummary, setBookingSummary] = useState<BookingReviewSummary | null>(null)
  const [bookingReviews, setBookingReviews] = useState<BookingReviewItem[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const loadData = useCallback(async () => {
    if (!propertyId) return
    try {
      const [reviewsRes, bookingRes, intelRes, seasonRes] = await Promise.allSettled([
        apiClient.getAirbnbReviews(propertyId),
        apiClient.getBookingReviews(propertyId),
        apiClient.getReviewIntelligence(propertyId),
        apiClient.getSeasonProfile(propertyId),
      ])
      if (reviewsRes.status === "fulfilled" && reviewsRes.value.ok) {
        setSource(reviewsRes.value.source)
        setSummary(reviewsRes.value.summary)
        setReviews(reviewsRes.value.reviews)
      }
      if (bookingRes.status === "fulfilled" && bookingRes.value.ok) {
        setBookingSource(bookingRes.value.source)
        setBookingSummary(bookingRes.value.summary)
        setBookingReviews(bookingRes.value.reviews)
      }
      if (intelRes.status === "fulfilled" && intelRes.value.ok) {
        setIntel(intelRes.value)
      }
      if (seasonRes.status === "fulfilled" && seasonRes.value.ok) {
        setSeasonProfile(seasonRes.value)
      }
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadData() }, [loadData])

  // Poll while any import is running
  useEffect(() => {
    if (source?.status !== "running" && bookingSource?.status !== "running") return
    const interval = setInterval(loadData, 10000)
    return () => clearInterval(interval)
  }, [source?.status, bookingSource?.status, loadData])

  // Poll while intelligence is computing
  useEffect(() => {
    if (!intel?.computing || intel?.available) return
    const interval = setInterval(async () => {
      if (!propertyId) return
      try {
        const res = await apiClient.getReviewIntelligence(propertyId)
        if (res.ok) setIntel(res)
      } catch { /* ignore */ }
    }, 8000)
    return () => clearInterval(interval)
  }, [intel?.computing, intel?.available, propertyId])

  // Poll while intelligence is stale or recomputing
  useEffect(() => {
    if (!intel?.stale && !(intel as any)?._recomputing) return
    const interval = setInterval(async () => {
      if (!propertyId) return
      try {
        const res = await apiClient.getReviewIntelligence(propertyId)
        if (res.ok) setIntel(res)
      } catch { /* ignore */ }
    }, 10000)
    return () => clearInterval(interval)
  }, [intel?.stale, (intel as any)?._recomputing, propertyId])

  // Force recompute intelligence
  const forceRecompute = useCallback(async () => {
    if (!propertyId) return
    setIntel((prev) => prev ? { ...prev, _recomputing: true } as any : prev)
    try {
      const res = await apiClient.recomputeReviewIntelligence(propertyId)
      if (res.ok && res.computing) {
        const fresh = await apiClient.getReviewIntelligence(propertyId)
        if (fresh.ok) setIntel(fresh)
      } else if (res.ok) {
        setIntel(res)
      } else {
        setIntel((prev) => prev ? { ...prev, _recomputing: false } as any : prev)
      }
    } catch {
      setIntel((prev) => prev ? { ...prev, _recomputing: false } as any : prev)
    }
  }, [propertyId])

  // Daily run cooldown — disable recompute for 24h after last run
  const recomputeCooldown = useMemo(() => {
    const computedAt = (intel as any)?.computed_at
    if (!computedAt) return false
    const ts = new Date(computedAt).getTime()
    if (Number.isNaN(ts)) return false
    return Date.now() - ts < 24 * 60 * 60 * 1000
  }, [(intel as any)?.computed_at])

  return {
    source, summary, reviews,
    bookingSource, bookingSummary, bookingReviews,
    intel, seasonProfile, loading, error, setError, refresh: loadData,
    forceRecompute, recomputeCooldown,
  }
}
